import type {
  AuthPost,
  AuthResult,
  Challenge,
  ChallengeRef,
  ClientMetadata,
  GoogleLoginRequest,
  LoginRequest,
  MfaMethod,
  PasskeyLoginRequest,
  StartVerification,
  VerificationPurpose,
  VerifiedResult,
} from './types';
import { parseAuthResponse, parseChallenge, parseVerified } from './response';

export { AuthRequestError, parseAuthResponse } from './response';
export * from './controller';
export * from './wording';

export const createAuthClient = ({ post }: { post: AuthPost }) => {
  const signIn = async (
    path: string,
    body: Record<string, unknown>
  ): Promise<AuthResult> => {
    const response = await post(path, body, { auth: false });
    return parseAuthResponse(response.status, response.body);
  };

  const send = async (
    path: string,
    body: Record<string, unknown>,
    purpose: VerificationPurpose
  ) => {
    const response = await post(path, body, { auth: purpose !== 'login' });
    return parseChallenge(response.status, response.body);
  };

  return {
    login: (input: LoginRequest, metadata: ClientMetadata = {}) =>
      signIn('/login', { ...input, ...metadata }),

    loginWithGoogle: (
      input: GoogleLoginRequest,
      metadata: ClientMetadata = {}
    ) => signIn('/auth/google', { ...input, ...metadata }),

    loginWithPasskey: (
      input: PasskeyLoginRequest,
      metadata: ClientMetadata = {}
    ) =>
      signIn('/auth/webauthn/verify-authentication', { ...input, ...metadata }),

    start: (input: StartVerification): Promise<Challenge> =>
      send('/auth/verification', input, input.purpose),

    resend: ({ purpose, token }: ChallengeRef): Promise<Challenge> =>
      send('/auth/verification/resend', { token }, purpose),

    switchTo: (
      { purpose, token }: ChallengeRef,
      method: MfaMethod
    ): Promise<Challenge> =>
      send('/auth/verification/switch', { token, method }, purpose),

    verify: async <P extends VerificationPurpose>(
      { purpose, token }: ChallengeRef<P>,
      code: string,
      metadata: ClientMetadata = {}
    ): Promise<VerifiedResult[P]> => {
      const { status, body } = await post(
        '/auth/verification/verify',
        { ...metadata, token, code },
        { auth: purpose !== 'login' }
      );
      return parseVerified(purpose, status, body);
    },
  };
};
